/*
 * Copyright 2026 Sony Corporation
 */

import { DeviceOpSchema, MAX_DEVICES, lookAtRotation, type DeviceOp, type Vec3 } from './schema';
import type { ApiClient } from '../services/api-client';
import { validateOptics, type Device, type Optics } from '@ips-device-verse/twin-service/schema';
import { resolveOptics, type ResolvedOptics } from '../scene/camera-optics';
import type { Transform } from '../scene/types';

/**
 * Device ops, run against the APIs that already exist (devplan §12 U4).
 *
 * No new service and no new write path: an added device is the same
 * `POST /devices` the dialog sends, and a pose is the same desired-state write
 * a gizmo drag makes — so an agent-driven move is indistinguishable downstream
 * from a drag, including for an offline gateway, where the shadow queues it.
 */

/**
 * The identity a device the assistant places starts with.
 *
 * Placing a device and binding it to a body are two different acts: the
 * assistant does the first, and the inspector is where someone does the second.
 * This is the same default the *Add device* dialog opens on, so an agent-added
 * device is in exactly the state a hand-added one is before its serial is typed
 * in — not a pretend device.
 */
const PLACEHOLDER_MODEL = 'ILCE-7CR';

const toVec = (vector: { x: number; y: number; z: number }): Vec3 => [vector.x, vector.y, vector.z];

const toTransform = (position: Vec3, rotation: Vec3): Transform => ({
  position: { x: position[0], y: position[1], z: position[2] },
  rotation: { x: rotation[0], y: rotation[1], z: rotation[2] },
});

export interface SceneBounds {
  center: Vec3;
  radius: number;
}

/**
 * The optics a change asks for, on top of what the device has.
 *
 * What the op does not name is fitted to the lens (switching to an 85 mm prime
 * moves the focal length with it); what it does name is kept as asked, so an
 * impossible value is refused with a reason rather than silently clamped.
 */
const fitOptics = (model: string, stored: Optics | undefined, change: Partial<Optics>): Optics | string => {
  const current = resolveOptics(model, stored).optics;
  const next = { ...resolveOptics(model, { ...current, ...change }).optics, ...change };
  return validateOptics(model, next) ?? next;
};

/** What a proposed device would see through, so its frustum and its coverage can be judged before Apply. */
export const proposedOptics = (op: Extract<DeviceOp, { op: 'add_device' }>): ResolvedOptics => {
  const model = op.model ?? PLACEHOLDER_MODEL;
  const fitted = op.optics ? fitOptics(model, undefined, op.optics) : undefined;
  return resolveOptics(model, typeof fitted === 'string' ? undefined : fitted);
};

/**
 * Where an `add_device` op puts the device.
 *
 * Used to draw the proposal and to write it, so what was reviewed in the
 * viewport is what gets created — an op is not allowed to mean two things.
 */
export const plannedTransform = (op: Extract<DeviceOp, { op: 'add_device' }>, bounds: SceneBounds): Transform => {
  const position = op.position ?? [bounds.center[0], bounds.center[1] + bounds.radius * 0.25, bounds.center[2]];
  const rotation = op.look_at ? lookAtRotation(position, op.look_at) : (op.rotation ?? [0, 0, 0]);
  return toTransform(position, rotation);
};

export interface ApplyCameraOpsDeps {
  api: ApiClient;
  worldId: string;
  devices: readonly Device[];
  /** Where each device stands, so a partial pose keeps the half it did not name. */
  poses: Record<string, Transform>;
  /** Somewhere to put a device the ops did not place. */
  bounds: SceneBounds;
}

/**
 * Runs a batch and reports what it could not do.
 *
 * One op at a time, and a failure is collected rather than thrown: twenty-three
 * devices placed and one refused is a better outcome than none placed, and the
 * message is the same thing the model would be handed to correct itself in P4.
 */
export const applyCameraOps = async (
  ops: readonly DeviceOp[],
  { api, worldId, devices, poses, bounds }: ApplyCameraOpsDeps
): Promise<string[]> => {
  const errors: string[] = [];
  let count = devices.length;
  let added = 0;

  const poseOf = (deviceId: string): Transform | undefined => poses[deviceId];

  for (const raw of ops) {
    // Parsed here too, not only at the boundary: this is the check that holds
    // whichever side of the interface the ops came from.
    const parsed = DeviceOpSchema.safeParse(raw);
    if (!parsed.success) {
      errors.push(parsed.error.issues[0]?.message ?? 'Unsupported operation.');
      continue;
    }
    const op = parsed.data;

    const known = op.op === 'add_device' ? undefined : devices.find((device) => device.device_id === op.device_id);
    if (op.op !== 'add_device' && !known) {
      errors.push(`No device with id ${op.device_id}.`);
      continue;
    }

    try {
      switch (op.op) {
        case 'add_device': {
          if (count >= MAX_DEVICES) {
            errors.push(`A world may hold at most ${MAX_DEVICES} devices.`);
            continue;
          }
          const model = op.model ?? PLACEHOLDER_MODEL;
          const optics = op.optics ? fitOptics(model, undefined, op.optics) : undefined;
          if (typeof optics === 'string') {
            errors.push(`${op.name ?? 'The new device'}: ${optics}.`);
            continue;
          }
          added += 1;
          // No address: the service allocates one, and one at a time is what
          // keeps a batch of eight from landing on the same one.
          await api.createDevice(worldId, {
            name: op.name ?? `Device ${count + 1}`,
            model,
            // Unique within the world so two placed devices are two rows, and
            // obviously not a real body so nobody reads it as one.
            serial: `PLACED-${Date.now().toString(36)}-${added}`,
            transform: plannedTransform(op, bounds),
            optics,
          });
          count += 1;
          break;
        }

        case 'set_pose': {
          const current = poseOf(op.device_id);
          const position = op.position ?? (current ? toVec(current.position) : undefined);
          const rotation = op.rotation ?? (current ? toVec(current.rotation) : undefined);
          if (!position || !rotation) {
            errors.push(`${known?.name ?? op.device_id} has no pose yet, so it has to be given a whole one.`);
            continue;
          }
          await api.writeDeviceState(worldId, op.device_id, { transform: toTransform(position, rotation) });
          break;
        }

        case 'look_at': {
          const current = poseOf(op.device_id);
          if (!current) {
            errors.push(`${known?.name ?? op.device_id} has no pose yet, so there is nothing to aim.`);
            continue;
          }
          const position = toVec(current.position);
          await api.writeDeviceState(worldId, op.device_id, {
            transform: toTransform(position, lookAtRotation(position, op.target)),
          });
          break;
        }

        case 'remove_device': {
          await api.deleteDevice(worldId, op.device_id);
          count -= 1;
          break;
        }

        case 'set_optics': {
          const { op: _op, device_id: deviceId, ...change } = op;
          const optics = fitOptics(known!.model, known!.optics, change);
          if (typeof optics === 'string') {
            errors.push(`${known!.name}: ${optics}.`);
            continue;
          }
          await api.updateDevice(worldId, deviceId, { optics });
          break;
        }
      }
    } catch (cause) {
      errors.push(cause instanceof Error ? cause.message : 'The write was refused.');
    }
  }

  return errors;
};
